import React, { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_82d90db9-8951-4b97-8cda-1af587d8e2b7/artifacts/yr1q4btp_quntrix_logo.png.png';

/* ---------- Reusable bits ---------- */

const MonoLabel = ({ children, className = '' }) => (
  <span className={`font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-500 ${className}`}>
    {children}
  </span>
);

const SectionHeader = ({ code, eyebrow, children }) => (
  <div className="border-t border-black grid grid-cols-12 items-start">
    <div className="col-span-12 md:col-span-2 py-6 md:py-8 border-r-0 md:border-r border-black md:pr-6">
      <MonoLabel>{code}</MonoLabel>
      <div className="font-mono-ed text-[11px] uppercase tracking-[0.18em] text-black mt-1">{eyebrow}</div>
    </div>
    <h2 className="col-span-12 md:col-span-10 py-6 md:py-8 md:pl-8 font-display font-extrabold text-3xl sm:text-5xl tracking-tight leading-[0.95]">
      {children}
    </h2>
  </div>
);

/* ---------- PQC Data ---------- */

const PQC_SERVICES = [
  {
    code: 'PQC-01',
    title: 'Cryptographic Discovery & Readiness Assessment',
    tagline: 'Know your exposure before adversaries do.',
    description:
      'A full audit of your cryptographic estate — applications, APIs, databases, TLS endpoints, PKI, HSMs. We identify every algorithm in use, map certificate lifecycles, and produce a Crypto Bill of Materials (CBOM). Output is a prioritised risk register quantifying exposure to harvest-now-decrypt-later (HNDL) attacks.',
    outcomes: ['Full cryptographic inventory (CBOM)', 'HNDL exposure risk score', 'Regulatory gap analysis (NIST PQC, DORA, NIS2)', 'Prioritised remediation roadmap'],
    standards: ['NIST IR 8547', 'ETSI GR QSC 006', 'ISO/IEC 18033'],
  },
  {
    code: 'PQC-02',
    title: 'Post-Quantum Cryptography Migration',
    tagline: 'Migrate to NIST-approved PQC algorithms — without downtime.',
    description:
      'End-to-end migration to NIST-standardised post-quantum algorithms: ML-KEM (CRYSTALS-Kyber), ML-DSA (CRYSTALS-Dilithium), SLH-DSA (SPHINCS+). Substitution across application layer, middleware, API gateways and infrastructure with zero-downtime canary deployment.',
    outcomes: ['ML-KEM / ML-DSA / SLH-DSA implementation', 'Zero-downtime migration strategy', 'Performance benchmarking', 'Cryptographic validation'],
    standards: ['FIPS 203', 'FIPS 204', 'FIPS 205', 'NIST SP 800-208'],
  },
  {
    code: 'PQC-03',
    title: 'Hybrid Cryptographic Transition Architecture',
    tagline: 'Classical + quantum-safe, running in parallel during transition.',
    description:
      'Hybrid architectures combining classical (RSA-2048, ECDH P-256) with PQC counterparts via composite keys and dual-algorithm TLS. Composite X.509 certificates ensure interoperability while delivering quantum resistance.',
    outcomes: ['Composite key + certificate design', 'Hybrid TLS 1.3 configuration', 'Dual-algorithm API gateway', 'Interoperability testing'],
    standards: ['IETF draft-ounsworth-pq-composite-keys', 'RFC 8446', 'X.509v3'],
  },
  {
    code: 'PQC-04',
    title: 'PKI & Certificate Lifecycle Modernisation',
    tagline: 'Quantum-safe PKI from root CA to leaf certificate.',
    description:
      'Redesign of root and intermediate CAs, OCSP responders, CRL distribution — using PQC-capable algorithms. Includes short-lived certificate automation (ACME), HSM upgrades for PQC key storage, and integration with AD CS, Vault, Keyfactor and Venafi.',
    outcomes: ['PQC-capable root and intermediate CAs', 'Automated certificate lifecycle (ACME/EST)', 'HSM PQC key storage', 'Expiry monitoring'],
    standards: ['RFC 9480', 'NIST SP 800-57', 'CAB/Forum BR'],
  },
  {
    code: 'PQC-05',
    title: 'Application & API Cryptographic Remediation',
    tagline: 'Fix vulnerable crypto at the code level — automatically.',
    description:
      'AI-assisted SAST across Java, Python, Go, Node, C/C++ codebases. Remediation engine generates pull requests with PQC-compliant replacements, plugs into GitHub Actions / GitLab CI / Jenkins, and produces SBOM-enriched security reports.',
    outcomes: ['Automated crypto vulnerability scanning', 'AI-generated PQC remediation PRs', 'CI/CD crypto enforcement gates', 'mTLS API gateway PQC policy'],
    standards: ['OWASP Crypto Cheat Sheet', 'NIST SSDF', 'CycloneDX CBOM'],
  },
  {
    code: 'PQC-06',
    title: 'Cloud & Infrastructure Security Modernisation',
    tagline: 'Quantum-safe security from network layer to cloud workloads.',
    description:
      'Upgrade IPsec / WireGuard VPN tunnels, configure Istio service mesh with PQC cipher suites, integrate AWS KMS / Azure Key Vault / GCP Cloud KMS with PQC-capable HSMs, and implement quantum-safe secrets management across multi-cloud.',
    outcomes: ['PQC-configured service mesh', 'Quantum-safe VPN tunnels', 'Cloud KMS + HSM PQC integration', 'Multi-cloud secrets modernisation'],
    standards: ['NIST SP 800-77 Rev 1', 'CISA PQC Guidance', 'CSA CCM v4'],
  },
  {
    code: 'PQC-07',
    title: 'Cryptographic Governance & Managed PQC Support',
    tagline: 'Ongoing quantum-safety as a managed service.',
    description:
      'Continuous cryptographic posture monitoring, algorithm deprecation alerts, NIST advisory tracking, quarterly health reports, and incident response for cryptographic failures. Establishment of a Cryptographic Centre of Excellence (CCoE).',
    outcomes: ['24/7 cryptographic posture monitoring', 'Algorithm deprecation alerts', 'Quarterly health reports', 'Regulatory audit support'],
    standards: ['NIST NCCoE PQC Project', 'ENISA PQC Guidelines', 'ISO/IEC 27001 A.10'],
  },
  {
    code: 'PQC-08',
    title: 'PQC Training, Workshops & Knowledge Transfer',
    tagline: 'Build internal quantum-safety expertise.',
    description:
      'Programmes from 2-hour executive briefings to 5-day technical bootcamps covering quantum computing fundamentals, lattice-based cryptography, hash-based signatures, and hands-on labs with liboqs, PQClean, Bouncy Castle PQC.',
    outcomes: ['Executive quantum threat awareness', 'Developer PQC bootcamp', 'Hands-on labs', 'Internal PQC standards playbook'],
    standards: ['NIST PQC Project documentation', 'IETF PQC WG specs'],
  },
];

const TIMELINE = [
  { year: '2024', label: 'NIST finalises PQC standards (FIPS 203/204/205).', tone: 'normal' },
  { year: 'NOW', label: 'Harvest-now-decrypt-later (HNDL) attacks already active.', tone: 'alert' },
  { year: '2026', label: 'US government PQC migration mandates begin.', tone: 'normal' },
  { year: '2029', label: 'Target: critical systems PQC-compliant.', tone: 'normal' },
  { year: '2030+', label: 'CRQC risk window opens.', tone: 'normal' },
];

const SPECS = [
  { metric: 'Qubits', value: '1,000+', note: 'Logical, error-corrected', },
  { metric: 'Gate fidelity', value: '99.9%', note: 'Two-qubit, median', },
  { metric: 'Coherence time', value: '500 μs', note: 'T₂ measured', },
  { metric: 'Quantum volume', value: '2,048', note: 'IBM QV benchmark', },
];

/* ---------- Components ---------- */

const Nav = ({ onNav }) => {
  const [open, setOpen] = useState(false);
  const items = [
    { id: 'about', label: 'About' },
    { id: 'solutions', label: 'Solutions' },
    { id: 'pqc', label: 'PQC' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'partnership', label: 'Partnership' },
  ];
  return (
    <header className="sticky top-0 z-50 bg-[#F7F7F6] border-b border-black">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-16">
        <button onClick={() => onNav('top')} className="flex items-center gap-3" data-testid="navbar-logo-button">
          <img src={LOGO_URL} alt="Quntrix" className="h-8 w-8 object-contain" data-testid="navbar-logo" />
          <div className="flex flex-col leading-none">
            <span className="font-display font-extrabold text-[15px] tracking-tight">Quntrix</span>
            <span className="font-mono-ed text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-0.5">Technologies</span>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {items.map((it) => (
            <button
              key={it.id}
              onClick={() => onNav(it.id)}
              className="font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-700 hover:text-black"
              data-testid={`nav-${it.id}`}
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => onNav('contact')}
            className="hidden md:inline-flex items-center gap-2 bg-black text-white px-4 py-2 font-mono-ed text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-black hover:outline hover:outline-1 hover:outline-black"
            data-testid="nav-get-in-touch"
          >
            Get in touch <span aria-hidden>→</span>
          </button>
          <button onClick={() => setOpen(!open)} className="md:hidden font-mono-ed text-[11px] uppercase tracking-[0.18em]" data-testid="mobile-menu-button">
            {open ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-black px-6 py-4 space-y-3" data-testid="mobile-menu">
          {items.map((it) => (
            <button key={it.id} onClick={() => { onNav(it.id); setOpen(false); }} className="block font-mono-ed text-[12px] uppercase tracking-[0.18em]">
              {it.label}
            </button>
          ))}
          <button onClick={() => { onNav('contact'); setOpen(false); }} className="block font-mono-ed text-[12px] uppercase tracking-[0.18em] underline underline-offset-4">
            Get in touch →
          </button>
        </div>
      )}
    </header>
  );
};

const Hero = ({ onNav }) => (
  <section id="top" className="border-b border-black bg-[#F7F7F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-px bg-black">
      {/* Left: editorial column */}
      <div className="col-span-12 lg:col-span-8 bg-[#F7F7F6] py-12 md:py-20 lg:py-28 pr-0 lg:pr-12">
        <div className="flex items-center gap-3 mb-10">
          <MonoLabel>Sec. 00 / Index</MonoLabel>
          <span className="h-px flex-1 bg-black/20" />
          <MonoLabel>Bangalore · Toronto</MonoLabel>
        </div>
        <h1 className="font-display font-extrabold text-5xl sm:text-7xl lg:text-[88px] leading-[0.92] tracking-[-0.02em]" data-testid="hero-title">
          Harnessing quantum power<br />
          <span className="italic font-serif-ed font-normal text-neutral-500">for the next</span> generation of computing.
        </h1>
        <div className="mt-10 max-w-[620px] font-serif-ed text-lg leading-[1.6] text-neutral-800" data-testid="hero-lede">
          <span className="font-display float-left text-7xl leading-[0.8] pr-3 pt-1">Q</span>
          untrix builds the cryptographic plumbing and quantum hardware partnerships that
          enterprises will need before a cryptographically-relevant quantum computer arrives.
          We work with security teams, PKI architects, and CISOs in finance, defence and
          healthcare — quietly, in plain English, with measurable deliverables.
        </div>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={() => onNav('pqc')}
            className="bg-black text-white px-6 py-3 font-mono-ed text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-black hover:outline hover:outline-1 hover:outline-black"
            data-testid="hero-cta-pqc"
          >
            View PQC services →
          </button>
          <button
            onClick={() => onNav('partnership')}
            className="bg-transparent text-black px-6 py-3 font-mono-ed text-[11px] uppercase tracking-[0.18em] outline outline-1 outline-black hover:bg-black hover:text-white"
            data-testid="hero-cta-partnership"
          >
            SuperQ partnership
          </button>
        </div>
      </div>

      {/* Right: metadata panel */}
      <aside className="col-span-12 lg:col-span-4 bg-white py-12 md:py-20 lg:py-28 px-6 md:px-10 relative">
        <div className="absolute inset-0 qx-dots opacity-[0.06]" aria-hidden></div>
        <div className="relative">
          <div className="flex items-baseline justify-between border-b border-black pb-3 mb-6">
            <MonoLabel className="!text-black">File · QX-2026</MonoLabel>
            <MonoLabel>v. 4.2</MonoLabel>
          </div>
          <img src={LOGO_URL} alt="Quntrix Technologies" className="h-40 w-40 object-contain mx-auto mb-8" data-testid="hero-logo" />
          <dl className="font-mono-ed text-[12px] divide-y divide-black/20 border-t border-b border-black">
            <div className="flex justify-between py-2.5">
              <dt className="text-neutral-500">Founded</dt><dd>2024</dd>
            </div>
            <div className="flex justify-between py-2.5">
              <dt className="text-neutral-500">HQ</dt><dd>Bangalore, IN</dd>
            </div>
            <div className="flex justify-between py-2.5">
              <dt className="text-neutral-500">Partner</dt><dd>SuperQ · Canada</dd>
            </div>
            <div className="flex justify-between py-2.5">
              <dt className="text-neutral-500">Focus</dt><dd>PQC · Quantum HW</dd>
            </div>
            <div className="flex justify-between py-2.5">
              <dt className="text-neutral-500">Stage</dt><dd>Series-pre</dd>
            </div>
          </dl>
          <p className="mt-6 font-serif-ed text-[15px] italic text-neutral-700 leading-snug">
            “Harnessing quantum power for next-gen computing.”
          </p>
        </div>
      </aside>
    </div>

    {/* Marquee ticker */}
    <div className="border-t border-black bg-black text-[#F7F7F6] overflow-hidden">
      <div className="qx-ticker flex gap-12 whitespace-nowrap py-2.5 font-mono-ed text-[11px] uppercase tracking-[0.2em]">
        {Array.from({ length: 2 }).map((_, k) => (
          <React.Fragment key={k}>
            <span>NIST FIPS 203 · ML-KEM</span><span aria-hidden>◆</span>
            <span>NIST FIPS 204 · ML-DSA</span><span aria-hidden>◆</span>
            <span>NIST FIPS 205 · SLH-DSA</span><span aria-hidden>◆</span>
            <span>Harvest-now-decrypt-later</span><span aria-hidden>◆</span>
            <span>Crypto-agility</span><span aria-hidden>◆</span>
            <span>PKI modernisation</span><span aria-hidden>◆</span>
            <span>SuperQ × Quntrix</span><span aria-hidden>◆</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="bg-[#F7F7F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionHeader code="Sec. 01" eyebrow="About">
        A boutique deep-tech firm.<br />
        <span className="italic font-serif-ed font-normal text-neutral-500">Engineers first, then everything else.</span>
      </SectionHeader>

      <div className="border-t border-black grid grid-cols-12 gap-px bg-black">
        <article className="col-span-12 md:col-span-7 bg-[#F7F7F6] p-8 md:p-12">
          <MonoLabel>01.1 · Mission</MonoLabel>
          <p className="font-serif-ed text-xl md:text-2xl leading-[1.55] mt-4 text-neutral-900">
            We make quantum-safe cryptography unremarkable. The boring kind that just works,
            integrates with your existing PKI, and survives audits. We are not selling
            quantum mysticism — we are selling competent migrations.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-px bg-black border border-black">
            <div className="bg-[#F7F7F6] p-6">
              <MonoLabel>01.2 · Vision</MonoLabel>
              <p className="font-serif-ed text-base leading-[1.6] mt-2">
                A world where moving from RSA-2048 to ML-KEM is as routine as renewing a TLS certificate.
              </p>
            </div>
            <div className="bg-[#F7F7F6] p-6">
              <MonoLabel>01.3 · Expertise</MonoLabel>
              <p className="font-serif-ed text-base leading-[1.6] mt-2">
                Cryptographers, PKI architects, FPGA engineers, and quantum-hardware partners.
              </p>
            </div>
          </div>
        </article>

        <aside className="col-span-12 md:col-span-5 bg-white p-8 md:p-12">
          <MonoLabel>01.4 · Capabilities</MonoLabel>
          <ul className="mt-6 font-mono-ed text-[13px] divide-y divide-black/15 border-t border-b border-black/40">
            {[
              ['Discovery', 'Crypto Bill of Materials'],
              ['Migration', 'ML-KEM · ML-DSA · SLH-DSA'],
              ['Hybrid', 'Classical + PQC composite'],
              ['PKI', 'Root-to-leaf modernisation'],
              ['SAST', 'AI-assisted code remediation'],
              ['Cloud', 'Service-mesh PQC enforcement'],
              ['Governance', 'CCoE & NIST advisory'],
              ['Training', 'Workshops & bootcamps'],
            ].map(([k, v], i) => (
              <li key={k} className="flex items-baseline justify-between py-2.5">
                <span className="text-neutral-500">{String(i + 1).padStart(2, '0')} · {k}</span>
                <span>{v}</span>
              </li>
            ))}
          </ul>
          <p className="font-serif-ed text-sm italic text-neutral-600 mt-6">
            Quntrix is a partner, not a vendor. We sign NDAs in the morning and ship migration plans by Friday.
          </p>
        </aside>
      </div>
    </div>
  </section>
);

const Solutions = () => (
  <section id="solutions" className="bg-[#F7F7F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionHeader code="Sec. 02" eyebrow="Solutions / Specs">
        Hardware specs &mdash; <span className="italic font-serif-ed font-normal text-neutral-500">measured, not marketed.</span>
      </SectionHeader>

      <div className="border-t border-black grid grid-cols-12 gap-px bg-black">
        <div className="col-span-12 md:col-span-5 bg-[#F7F7F6] p-8 md:p-12">
          <p className="font-serif-ed text-lg leading-[1.6] text-neutral-800">
            Through our partnership with <span className="font-display font-extrabold">SuperQ (Canada)</span>, we operate one of the
            most accessible superconducting quantum stacks outside North America &mdash; with
            published benchmarks, not press releases.
          </p>
          <p className="font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-500 mt-6">
            Numbers below are independently verified. <br /> Last calibration · 14 Feb 2026.
          </p>
        </div>
        <div className="col-span-12 md:col-span-7 bg-white">
          <table className="w-full font-mono-ed text-sm">
            <thead>
              <tr className="border-b border-black">
                <th className="text-left py-4 px-6 uppercase tracking-[0.18em] text-[11px] font-medium">Metric</th>
                <th className="text-left py-4 px-6 uppercase tracking-[0.18em] text-[11px] font-medium">Value</th>
                <th className="text-left py-4 px-6 uppercase tracking-[0.18em] text-[11px] font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {SPECS.map((s, i) => (
                <tr key={s.metric} className={i < SPECS.length - 1 ? 'border-b border-black/15' : ''} data-testid={`spec-row-${i}`}>
                  <td className="py-5 px-6 text-neutral-500">{s.metric}</td>
                  <td className="py-5 px-6 font-display font-extrabold text-2xl tracking-tight text-black">{s.value}</td>
                  <td className="py-5 px-6 text-neutral-700">{s.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
);

const PQCRow = ({ s, idx, openIdx, setOpenIdx }) => {
  const open = openIdx === idx;
  const outcomes = s.outcomes;
  const standards = s.standards;
  return (
    <div className="border-b border-black" data-testid={`pqc-row-${s.code.toLowerCase()}`}>
      <button
        onClick={() => setOpenIdx(open ? -1 : idx)}
        className={`w-full grid grid-cols-12 gap-4 md:gap-6 items-baseline text-left py-6 md:py-7 px-1 hover:bg-white transition-none ${open ? 'bg-white' : ''}`}
        data-testid={`pqc-trigger-${s.code.toLowerCase()}`}
      >
        <span className="col-span-2 md:col-span-1 font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-500">{s.code}</span>
        <span className="col-span-10 md:col-span-6 font-display font-extrabold text-lg md:text-xl tracking-tight leading-snug">{s.title}</span>
        <span className="hidden md:block md:col-span-4 font-serif-ed text-[15px] italic text-neutral-600">{s.tagline}</span>
        <span className="col-span-12 md:col-span-1 text-right font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-500">
          {open ? '— Close' : '+ Open'}
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-12 gap-px bg-black border-t border-black" data-testid={`pqc-panel-${s.code.toLowerCase()}`}>
          <div className="col-span-12 md:col-span-7 bg-white p-6 md:p-8">
            <MonoLabel>{s.code}.1 · Brief</MonoLabel>
            <p className="font-serif-ed text-base leading-[1.65] mt-3 text-neutral-800">{s.description}</p>
          </div>
          <div className="col-span-12 md:col-span-3 bg-[#F7F7F6] p-6 md:p-8">
            <MonoLabel>{s.code}.2 · Outcomes</MonoLabel>
            <ul className="font-mono-ed text-[12px] mt-3 space-y-1.5">
              {outcomes.map((o, i) => (
                <li key={i} className="flex gap-2"><span className="text-neutral-400">{String(i + 1).padStart(2, '0')}</span><span>{o}</span></li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-2 bg-white p-6 md:p-8">
            <MonoLabel>{s.code}.3 · Standards</MonoLabel>
            <ul className="font-mono-ed text-[12px] mt-3 space-y-1.5">
              {standards.map((st, i) => (
                <li key={i}>{st}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const PQC = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="pqc" className="bg-[#F7F7F6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeader code="Sec. 03" eyebrow="Post-Quantum Cryptography">
          Eight services. <span className="italic font-serif-ed font-normal text-neutral-500">No buzzwords.</span>
        </SectionHeader>

        <div className="border-t border-black grid grid-cols-12 gap-px bg-black">
          <p className="col-span-12 md:col-span-7 bg-[#F7F7F6] p-8 md:p-12 font-serif-ed text-lg leading-[1.6] text-neutral-800">
            We move enterprises from RSA/ECC to the NIST-approved post-quantum suite —
            ML-KEM, ML-DSA, SLH-DSA — without breaking interoperability with the partners
            who haven&rsquo;t upgraded yet. Pick the row that matches your problem.
          </p>
          <div className="col-span-12 md:col-span-5 bg-white p-8 md:p-12">
            <MonoLabel>03.0 · Engagement model</MonoLabel>
            <dl className="font-mono-ed text-[12px] mt-4 divide-y divide-black/15 border-t border-b border-black/40">
              <div className="flex justify-between py-2.5"><dt className="text-neutral-500">Discovery</dt><dd>2&ndash;4 weeks</dd></div>
              <div className="flex justify-between py-2.5"><dt className="text-neutral-500">Pilot migration</dt><dd>6&ndash;12 weeks</dd></div>
              <div className="flex justify-between py-2.5"><dt className="text-neutral-500">Full rollout</dt><dd>9&ndash;18 months</dd></div>
              <div className="flex justify-between py-2.5"><dt className="text-neutral-500">Contact</dt><dd>pqc@quntrix.co.in</dd></div>
            </dl>
          </div>
        </div>

        <div className="border-t border-black bg-[#F7F7F6]">
          {PQC_SERVICES.map((s, i) => (
            <PQCRow key={s.code} s={s} idx={i} openIdx={openIdx} setOpenIdx={setOpenIdx} />
          ))}
        </div>
      </div>
    </section>
  );
};

const Timeline = () => (
  <section id="timeline" className="bg-[#F7F7F6]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionHeader code="Sec. 04" eyebrow="Quantum Threat Timeline">
        The clock is already running.
      </SectionHeader>

      <div className="border-t border-black bg-white">
        <ol className="divide-y divide-black/15">
          {TIMELINE.map((t, i) => {
            const isAlert = t.tone === 'alert';
            return (
              <li key={t.year} className="grid grid-cols-12 gap-4 md:gap-6 px-1 py-6 md:py-8" data-testid={`timeline-${i}`}>
                <span className={`col-span-3 md:col-span-2 font-display font-extrabold text-3xl md:text-5xl tracking-tight leading-none ${isAlert ? 'text-[#FF3B30]' : 'text-black'}`}>
                  {t.year}
                </span>
                <span className="col-span-9 md:col-span-9 font-serif-ed text-lg md:text-xl leading-[1.45] text-neutral-800">
                  {t.label}
                </span>
                <span className={`col-span-12 md:col-span-1 text-right font-mono-ed text-[11px] uppercase tracking-[0.18em] ${isAlert ? 'text-[#FF3B30]' : 'text-neutral-400'}`}>
                  {isAlert ? '◆ Live' : `T+${i}`}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  </section>
);

const Forms = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', message: '' });
  const [partnershipForm, setPartnershipForm] = useState({ company_name: '', contact_name: '', email: '', phone: '', inquiry_type: 'Technology Partnership', message: '' });
  const [loading, setLoading] = useState({ contact: false, partnership: false });

  const errMsg = (error) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : 'field';
      return `${field}: ${first?.msg || 'invalid value'}`;
    }
    if (typeof detail === 'string') return detail;
    return 'Failed. Please check your inputs and try again.';
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setLoading((l) => ({ ...l, contact: true }));
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Message received. We will be in touch.');
      setContactForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading((l) => ({ ...l, contact: false }));
    }
  };

  const submitPartnership = async (e) => {
    e.preventDefault();
    setLoading((l) => ({ ...l, partnership: true }));
    try {
      await axios.post(`${API}/partnership-inquiry`, partnershipForm);
      toast.success('Partnership inquiry received.');
      setPartnershipForm({ company_name: '', contact_name: '', email: '', phone: '', inquiry_type: 'Technology Partnership', message: '' });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading((l) => ({ ...l, partnership: false }));
    }
  };

  const inputCls = 'w-full bg-transparent border border-black px-3 py-3 font-mono-ed text-[13px] placeholder:text-neutral-400 focus:bg-white';
  const labelCls = 'font-mono-ed text-[10px] uppercase tracking-[0.2em] text-neutral-600 block mb-1.5';

  return (
    <section id="contact" className="bg-[#F7F7F6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <SectionHeader code="Sec. 05" eyebrow="Forms · Contact">
          Get in touch. Not a chatbot.
        </SectionHeader>

        <div id="partnership" className="border-t border-black grid grid-cols-12 gap-px bg-black">
          {/* Partnership form */}
          <form onSubmit={submitPartnership} className="col-span-12 lg:col-span-6 bg-[#F7F7F6] p-8 md:p-12 space-y-5" data-testid="partnership-form">
            <div className="flex items-baseline justify-between border-b border-black pb-3">
              <MonoLabel className="!text-black">05.1 · Partnership inquiry</MonoLabel>
              <MonoLabel>Required</MonoLabel>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Company *</label>
                <input required value={partnershipForm.company_name} onChange={(e) => setPartnershipForm({ ...partnershipForm, company_name: e.target.value })} className={inputCls} placeholder="ACME Corp." data-testid="partnership-company" />
              </div>
              <div>
                <label className={labelCls}>Contact name *</label>
                <input required value={partnershipForm.contact_name} onChange={(e) => setPartnershipForm({ ...partnershipForm, contact_name: e.target.value })} className={inputCls} placeholder="Jane Doe" data-testid="partnership-contact" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input required type="email" value={partnershipForm.email} onChange={(e) => setPartnershipForm({ ...partnershipForm, email: e.target.value })} className={inputCls} placeholder="jane@acme.com" data-testid="partnership-email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input value={partnershipForm.phone} onChange={(e) => setPartnershipForm({ ...partnershipForm, phone: e.target.value })} className={inputCls} placeholder="+91 …" data-testid="partnership-phone" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Inquiry type *</label>
              <select required value={partnershipForm.inquiry_type} onChange={(e) => setPartnershipForm({ ...partnershipForm, inquiry_type: e.target.value })} className={inputCls} data-testid="partnership-type">
                <option>Technology Partnership</option>
                <option>Research Collaboration</option>
                <option>PQC Migration</option>
                <option>Distribution / Reseller</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Message *</label>
              <textarea required rows={4} value={partnershipForm.message} onChange={(e) => setPartnershipForm({ ...partnershipForm, message: e.target.value })} className={inputCls} placeholder="Tell us about your problem in two paragraphs." data-testid="partnership-message" />
            </div>
            <button disabled={loading.partnership} className="w-full bg-black text-white py-3 font-mono-ed text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-black hover:outline hover:outline-1 hover:outline-black disabled:opacity-50" data-testid="partnership-submit">
              {loading.partnership ? 'Submitting…' : 'Submit inquiry →'}
            </button>
          </form>

          {/* Contact form */}
          <form onSubmit={submitContact} className="col-span-12 lg:col-span-6 bg-white p-8 md:p-12 space-y-5" data-testid="contact-form">
            <div className="flex items-baseline justify-between border-b border-black pb-3">
              <MonoLabel className="!text-black">05.2 · Direct contact</MonoLabel>
              <MonoLabel>{`<24h reply`}</MonoLabel>
            </div>
            <div>
              <label className={labelCls}>Your name *</label>
              <input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className={inputCls} placeholder="Full name" data-testid="contact-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Email *</label>
                <input required type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className={inputCls} placeholder="you@company.com" data-testid="contact-email" />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <input value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} className={inputCls} placeholder="Optional" data-testid="contact-company" />
              </div>
            </div>
            <div>
              <label className={labelCls}>What do you need? *</label>
              <textarea required rows={5} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className={inputCls} placeholder="A short description is enough — we will follow up." data-testid="contact-message" />
            </div>
            <button disabled={loading.contact} className="w-full bg-black text-white py-3 font-mono-ed text-[11px] uppercase tracking-[0.18em] hover:bg-white hover:text-black hover:outline hover:outline-1 hover:outline-black disabled:opacity-50" data-testid="contact-submit">
              {loading.contact ? 'Sending…' : 'Send message →'}
            </button>
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-neutral-500 pt-2 border-t border-black/15">
              info@quntrix.co.in &middot; +91 888401002 &middot; Bangalore, IN
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ onNav }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Subscribed.');
      setEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400) toast.error('Already subscribed.');
      else toast.error(typeof detail === 'string' ? detail : 'Subscribe failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-black text-[#F7F7F6] border-t border-black" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-10">
        <div className="grid grid-cols-12 gap-x-6 gap-y-12">
          {/* Massive wordmark */}
          <div className="col-span-12 lg:col-span-7">
            <MonoLabel className="!text-neutral-400">Sec. ∞ / Colophon</MonoLabel>
            <h2 className="font-display font-extrabold text-[18vw] lg:text-[14vw] leading-[0.85] tracking-[-0.04em] mt-4 select-none">
              QUNTRIX
            </h2>
            <p className="font-serif-ed italic text-lg text-neutral-300 mt-4 max-w-xl">
              Harnessing quantum power for next-gen computing &mdash; from a small office in
              Bangalore with a calibration log and a kettle.
            </p>
          </div>

          {/* Newsletter + meta */}
          <div className="col-span-12 lg:col-span-5 lg:pl-8 lg:border-l border-neutral-700">
            <MonoLabel className="!text-neutral-400">Subscribe</MonoLabel>
            <form onSubmit={subscribe} className="mt-3 flex border border-[#F7F7F6]" data-testid="newsletter-form">
              <input
                required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 bg-transparent px-3 py-3 font-mono-ed text-[13px] placeholder:text-neutral-500 text-[#F7F7F6]"
                data-testid="newsletter-email"
              />
              <button disabled={busy} className="bg-[#F7F7F6] text-black px-5 font-mono-ed text-[11px] uppercase tracking-[0.18em] hover:bg-black hover:text-[#F7F7F6] hover:outline hover:outline-1 hover:outline-[#F7F7F6] disabled:opacity-50" data-testid="newsletter-submit">
                {busy ? '…' : 'Join'}
              </button>
            </form>
            <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-3">
              Quarterly. No spam. Unsubscribe in one click.
            </p>

            <dl className="font-mono-ed text-[12px] mt-10 grid grid-cols-2 gap-y-3">
              <dt className="text-neutral-500">General</dt><dd>info@quntrix.co.in</dd>
              <dt className="text-neutral-500">PQC</dt><dd>pqc@quntrix.co.in</dd>
              <dt className="text-neutral-500">Phone</dt><dd>+91 888401002</dd>
              <dt className="text-neutral-500">HQ</dt><dd>Bangalore, IN</dd>
              <dt className="text-neutral-500">Partner</dt><dd>SuperQ · Toronto, CA</dd>
            </dl>
          </div>
        </div>

        <div className="border-t border-neutral-800 mt-12 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-6 w-6 object-contain" />
            <span className="font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-400">
              © 2026 Quntrix Technologies Pvt Ltd. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6 font-mono-ed text-[11px] uppercase tracking-[0.18em] text-neutral-400">
            <button onClick={() => onNav('top')} className="hover:text-[#F7F7F6]">Back to top ↑</button>
            <a className="hover:text-[#F7F7F6]" href="#">Privacy</a>
            <a className="hover:text-[#F7F7F6]" href="#">Terms</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ---------- Page ---------- */

const Home = () => {
  const onNav = (id) => {
    if (id === 'top') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <div className="min-h-screen bg-[#F7F7F6] text-black">
      <Toaster position="top-right" richColors />
      <Nav onNav={onNav} />
      <Hero onNav={onNav} />
      <About />
      <Solutions />
      <PQC />
      <Timeline />
      <Forms />
      <Footer onNav={onNav} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
